import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';
import { NAVBAR_MENU_KEYS, NS } from '../../../i18n/constants/keys';
import { Permission } from '../../../utils/helpers/permissions';
import PermissionGate from '../../../utils/hooks/PermissionGate';

const settingsItems = [
  {
    titleKey: NAVBAR_MENU_KEYS.systemSettings.items.usersRoles.title,
    path: '/access-management',
    permissions: [Permission.EMPLOYEE_VIEW, Permission.USERS_VIEW],
  },
  {
    titleKey: NAVBAR_MENU_KEYS.systemSettings.items.assessmentFramework.title,
    path: '/assessment-framework',
    permissions: [Permission.EMPLOYEE_VIEW, Permission.ASSESSMENTS_VIEW],
  },
  {
    titleKey: NAVBAR_MENU_KEYS.systemSettings.items.organizationSettings.title,
    path: '/organization-settings',
    permissions: [Permission.ORGUNIT_VIEW],
  },
  {
    titleKey: NAVBAR_MENU_KEYS.systemSettings.items.employeeManagement.title,
    path: '/employee-management',
    permissions: [Permission.EMPLOYEE_VIEW],
  },
  {
    titleKey: NAVBAR_MENU_KEYS.systemSettings.items.candidateManagement.title,
    path: '/candidate-management',
    permissions: [Permission.CANDIDATE_VIEW],
  },
  {
    titleKey: NAVBAR_MENU_KEYS.systemSettings.items.partnerManagement.title,
    path: '/partner-management',
    permissions: [Permission.PARTNER_VIEW],
  },
];

interface SettingsSubNavProps {
  currentPath: string;
}

export const SettingsSubNav: React.FC<SettingsSubNavProps> = ({ currentPath }) => {
  const { t } = useTranslation(NS.navbarMenus);
  const currentItem = settingsItems.find((item) => item.path === currentPath);

  if (!currentItem) {
    return null;
  }

  return (
    <Box
      component='nav'
      aria-label='Settings navigation'
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: { xs: 'flex-start', lg: 'center' },
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'divider',
        py: 1.5,
        gap: 1.5,
      }}
    >
      <Breadcrumbs
        separator={
          <Typography sx={{ color: 'text.disabled', fontSize: '0.875rem' }}>
            /
          </Typography>
        }
        aria-label='breadcrumb'
      >
        <Link
          to='/system-settings'
          style={{ textDecoration: 'none' }}
        >
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: 'text.secondary',
              fontWeight: 500,
              transition: 'color 0.15s ease',
              '&:hover': {
                color: 'primary.main',
                textDecoration: 'underline',
              },
            }}
          >
            {t(NAVBAR_MENU_KEYS.systemSettings.title)}
          </Typography>
        </Link>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#093725', letterSpacing: '-0.01em' }}>
          {t(currentItem.titleKey)}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {settingsItems
          .filter((item) => item.path !== currentPath)
          .map((item) => {
            const link = (
              <Link
                key={item.path}
                to={item.path}
                style={{ textDecoration: 'none' }}
              >
                <Box
                  component='span'
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    borderRadius: '6px',
                    px: 1.25,
                    py: 0.5,
                    fontSize: { xs: '0.75rem', lg: '0.8125rem' },
                    fontWeight: 600,
                    color: '#093725',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: 'rgba(9, 55, 37, 0.08)',
                      color: '#041d13',
                    },
                  }}
                >
                  {t(item.titleKey)}
                </Box>
              </Link>
            );

            return item.permissions.length > 0 ? (
              <PermissionGate
                key={item.path}
                requiredPermissions={item.permissions}
                matchAnyPermission
              >
                {link}
              </PermissionGate>
            ) : (
              link
            );
          })}
      </Box>
    </Box>
  );
};
